import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

const displayValue = (value) => (value?.trim?.() ? value : '—')

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join(' ')
}

const Clients = () => {
    const [clients, setClients] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [expandedId, setExpandedId] = useState(null)

    const fetchClients = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.get('/clients/')
            setClients(response.data)
        } catch (err) {
            console.error('Failed to fetch clients', err)
            setError(getErrorMessage(err, 'Failed to load clients. Please try again.'))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchClients()
    }, [fetchClients])

    const toggleDetails = (clientId) => {
        setExpandedId((current) => (current === clientId ? null : clientId))
    }

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Clients</h4>
                            <p className="text-muted mb-0">View and manage your client contacts</p>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {!loading && !error && (
                                <span className="badge badge-soft-primary fs-13 px-3 py-2">
                                    {clients.length} {clients.length === 1 ? 'Client' : 'Clients'}
                                </span>
                            )}
                            <Link to="/create-client" className="btn btn-primary">
                                <i className="ti ti-plus me-1" />
                                Add Client
                            </Link>
                        </div>
                    </div>

                    <div className="card shadow-sm">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="fw-bold mb-0">All Clients</h5>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-white"
                                onClick={fetchClients}
                                disabled={loading}
                            >
                                <i className="ti ti-refresh me-1" />
                                Refresh
                            </button>
                        </div>

                        <div className="card-body">
                            {loading && (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <BeatLoader color="#3550DC" size={10} />
                                </div>
                            )}

                            {!loading && error && (
                                <div className="alert alert-danger mb-0" role="alert">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <span>
                                            <i className="ti ti-alert-circle me-2" />
                                            {error}
                                        </span>
                                        <button type="button" className="btn btn-sm btn-danger" onClick={fetchClients}>
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!loading && !error && clients.length === 0 && (
                                <div className="text-center py-5">
                                    <div className="avatar avatar-lg bg-light text-muted mx-auto mb-3">
                                        <i className="ti ti-users fs-24" />
                                    </div>
                                    <h6 className="fw-semibold mb-1">No clients found</h6>
                                    <p className="text-muted mb-3">Clients you add will appear here.</p>
                                    <Link to="/create-client" className="btn btn-primary">
                                        <i className="ti ti-plus me-1" />
                                        Add Client
                                    </Link>
                                </div>
                            )}

                            {!loading && !error && clients.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-hover table-nowrap border mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Company</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>City</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {clients.map((client) => {
                                                const isExpanded = expandedId === client.id

                                                return (
                                                    <React.Fragment key={client.id}>
                                                        <tr>
                                                            <td>
                                                                <span className="fw-medium text-dark">
                                                                    {displayValue(client.company?.name)}
                                                                </span>
                                                            </td>
                                                            <td>{displayValue(client.first_name)} {displayValue(client.last_name)}</td>
                                                            <td>
                                                                {client.company?.email ? (
                                                                    <a href={`mailto:${client.company.email}`} className="text-dark">
                                                                        {client.company.email}
                                                                    </a>
                                                                ) : (
                                                                    '—'
                                                                )}
                                                            </td>
                                                            <td>{displayValue(client.phone)}</td>
                                                            <td>{displayValue(client.city)}</td>
                                                            <td className="text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => toggleDetails(client.id)}
                                                                    aria-expanded={isExpanded}
                                                                >
                                                                    <i className={`ti ${isExpanded ? 'ti-chevron-up' : 'ti-chevron-down'} me-1`} />
                                                                    {isExpanded ? 'Hide Details' : 'Show Details'}
                                                                </button>
                                                            </td>
                                                        </tr>

                                                        {isExpanded && (
                                                            <tr className="bg-light">
                                                                <td colSpan={6} className="p-0">
                                                                    <div className="p-3 px-4 border-top">
                                                                        <div className="row g-3">
                                                                            <div className="col-md-4">
                                                                                <p className="text-muted fs-12 mb-1">Address Line 1</p>
                                                                                <p className="mb-0 fw-medium">{displayValue(client.address_line1)}</p>
                                                                            </div>
                                                                            <div className="col-md-4">
                                                                                <p className="text-muted fs-12 mb-1">Address Line 2</p>
                                                                                <p className="mb-0 fw-medium">{displayValue(client.address_line2)}</p>
                                                                            </div>
                                                                            <div className="col-md-4">
                                                                                <p className="text-muted fs-12 mb-1">Notes</p>
                                                                                <p className="mb-0 fw-medium">{displayValue(client.notes)}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default Clients
